import React, { useState } from 'react';
import { Wallet, Shield, TrendingUp, Zap, Sparkles, Bot, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

interface LandingPageProps {
  onEmailSubmit: (email: string, name: string) => void;
  isLoading: boolean;
}

export function LandingPage({ onEmailSubmit, isLoading }: LandingPageProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState({ email: '', name: '' });

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({ email: '', name: '' });
    
    // Validate
    let hasError = false;
    if (!name.trim()) {
      setErrors(prev => ({ ...prev, name: 'Name is required' }));
      hasError = true;
    }
    if (!email.trim()) {
      setErrors(prev => ({ ...prev, email: 'Email is required' }));
      hasError = true;
    } else if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      hasError = true;
    }
    
    if (!hasError) {
      onEmailSubmit(email, name);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a0a2e] to-[#0a0a0a] relative overflow-hidden">
      {/* Animated background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-fuchsia-600/10 rounded-full blur-3xl animate-pulse delay-500" />
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-violet-400/40 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
            }}
          />
        ))}
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-20">
        {/* Logo and title */}
        <div className="text-center mb-16 space-y-6">
          {/* Animated logo icon */}
          <div className="flex justify-center mb-8">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity animate-pulse" />
              <div className="relative bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 p-6 rounded-2xl shadow-2xl transform group-hover:scale-110 transition-transform duration-300">
                <Sparkles className="w-16 h-16 text-white" />
              </div>
            </div>
          </div>

          <h1 className="text-7xl font-black bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 bg-clip-text text-transparent mb-4 tracking-tight">
            Nardium
          </h1>
          
          <p className="text-2xl text-violet-300/90 max-w-2xl mx-auto mb-2">
            The Future of DeFi Wallets
          </p>
          
          <p className="text-gray-400 max-w-xl mx-auto">
            Connect your decentralized wallet and access automated trading, staking, gamification, and much more
          </p>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 max-w-6xl w-full">
          {[
            {
              icon: Bot,
              title: 'Trading Bots',
              description: 'AI-powered bots operating 24/7',
              color: 'from-violet-600 to-purple-600'
            },
            {
              icon: TrendingUp,
              title: 'Auto-Trading',
              description: 'Real-time automated buy/sell',
              color: 'from-purple-600 to-fuchsia-600'
            },
            {
              icon: Shield,
              title: 'Total Security',
              description: 'Decentralized wallet, you control',
              color: 'from-fuchsia-600 to-pink-600'
            },
            {
              icon: Zap,
              title: 'Fast & Efficient',
              description: 'Instant trade execution',
              color: 'from-pink-600 to-violet-600'
            },
          ].map((feature, index) => (
            <div
              key={index}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 rounded-xl blur-xl transition-opacity duration-300"
                style={{
                  background: `linear-gradient(to bottom right, ${feature.color})`,
                }}
              />
              <div className="relative bg-[#0a0a0a]/80 backdrop-blur-xl border border-violet-500/20 rounded-xl p-6 hover:border-violet-500/40 transition-all duration-300 group-hover:transform group-hover:scale-105">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} p-2.5 mb-4 shadow-lg`}>
                  <feature.icon className="w-full h-full text-white" />
                </div>
                <h3 className="text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Email Registration Form */}
        <div className="space-y-4 w-full max-w-md">
          <div className="bg-[#0a0a0a]/80 backdrop-blur-xl border border-violet-500/20 rounded-2xl p-8 shadow-2xl">
            <div className="flex justify-center mb-6">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full blur-lg opacity-75 group-hover:opacity-100 transition-opacity" />
                <div className="relative bg-gradient-to-br from-violet-600 to-purple-600 p-3 rounded-full">
                  <Mail className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
            
            <h2 className="text-2xl text-white mb-2 text-center">
              Welcome to Nardium
            </h2>
            <p className="text-sm text-gray-400 text-center mb-6">
              Enter your details to access the platform
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Input */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm text-gray-300 block">
                  Full Name
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  disabled={isLoading}
                  className={`w-full h-12 px-4 bg-[#1a1a1a] border ${
                    errors.name ? 'border-red-500' : 'border-violet-500/20'
                  } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                />
                {errors.name && (
                  <p className="text-xs text-red-400">{errors.name}</p>
                )}
              </div>

              {/* Email Input */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm text-gray-300 block">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={isLoading}
                  className={`w-full h-12 px-4 bg-[#1a1a1a] border ${
                    errors.email ? 'border-red-500' : 'border-violet-500/20'
                  } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                />
                {errors.email && (
                  <p className="text-xs text-red-400">{errors.email}</p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 hover:from-violet-700 hover:via-purple-700 hover:to-fuchsia-700 text-white border-0 shadow-lg hover:shadow-violet-500/50 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="text-lg">
                    {isLoading ? 'Accessing...' : 'Access Nardium'}
                  </span>
                  {!isLoading && (
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  )}
                </div>
              </Button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-6">
              By accessing Nardium, you agree to our platform terms.
              <br />
              <span className="text-violet-400">Your data is secure and never shared.</span>
            </p>
          </div>

          {/* Wallet connection coming soon badge */}
          <div className="text-center">
            <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
              <Wallet className="w-3 h-3" />
              Wallet connection coming soon
            </p>
          </div>
        </div>

        {/* Bottom info */}
        <div className="mt-16 text-center">
          <p className="text-sm text-gray-500">
            🔐 100% Decentralized • 🚀 Non-Custodial • ⚡ Full Control
          </p>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.8;
          }
        }
        
        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </div>
  );
}